import { Command } from 'cmdk';
import { ChevronsLeft, ChevronsUpDown, Languages, LogOut, Menu, Moon, Search, Shield, Sun, TerminalSquare } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, Kbd } from '@/components/ui/misc';
import { Dialog, DropdownContent, DropdownItem, DropdownLabel, DropdownMenu, DropdownSeparator, DropdownTrigger } from '@/components/ui/overlay';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { NAV, PLATFORM_NAV, type NavItem } from './nav';
import { useQuery } from '@tanstack/react-query';
import { loadEntityList, ENTITIES } from '@/lib/entities';

function useVisibleNav() {
  const { can, features, tenant } = useAuth();
  return useMemo(
    () => (!tenant ? [] : NAV).map((s) => ({ ...s, items: s.items.filter((i) => (!i.permission || can(i.permission)) && (!i.feature || features.has(i.feature))) })).filter((s) => s.items.length),
    [can, features, tenant],
  );
}

const TITLES = new Map([...NAV.flatMap((s) => s.items), ...PLATFORM_NAV].map((i) => [i.to, i.label]));

/** Tab title from the navigation; pages with a more specific title (a person, a horse) set their own once loaded. */
function useDocumentTitle(pathname: string) {
  useEffect(() => {
    const label = TITLES.get(pathname) ?? TITLES.get(`/${pathname.split('/')[1] ?? ''}`);
    if (label && (pathname === '/' || pathname.split('/').length <= 2 || pathname.startsWith('/platform'))) document.title = `${label} · ${pathname.startsWith('/platform') ? 'Platform' : 'Stable Admin'}`;
  }, [pathname]);
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('stable.sidebar') === 'collapsed');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useDocumentTitle(location.pathname);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => localStorage.setItem('stable.sidebar', collapsed ? 'collapsed' : 'open'), [collapsed]);

  return (
    <div className="flex min-h-screen">
      <aside className={cn('fixed inset-y-0 start-0 z-40 hidden flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200 lg:flex', collapsed ? 'w-[68px]' : 'w-[248px]')}>
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 start-0 flex w-[260px] flex-col bg-sidebar text-sidebar-foreground shadow-lift">
            <Sidebar collapsed={false} />
          </aside>
        </div>
      )}
      <div className={cn('flex min-w-0 flex-1 flex-col transition-[padding] duration-200', collapsed ? 'lg:ps-[68px]' : 'lg:ps-[248px]')}>
        <TopBar onMenu={() => setMobileOpen(true)} onSearch={() => setPaletteOpen(true)} />
        <main className="mx-auto w-full max-w-[1480px] flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

/** Tab title and favicon follow the signed-in stable (the static ones cover the sign-in page). */
function useBrandedDocument(name?: string | null, logoUrl?: string | null) {
  useEffect(() => {
    if (name) document.title = `${name} · Admin`;
    if (!logoUrl) return;
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) {
      link.type = '';
      link.href = logoUrl;
    }
  }, [name, logoUrl]);
}

function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle?: () => void }) {
  const { branding, me, isPlatformAdmin } = useAuth();
  const { t } = useI18n();
  const nav = useVisibleNav();
  useBrandedDocument(branding?.name ?? me?.tenant.name, branding?.logo?.thumbUrl ?? branding?.logo?.url);
  return (
    <>
      <div className="flex h-16 shrink-0 items-center gap-3 border-b border-white/5 px-4">
        {branding?.logo ? <img src={branding.logo.thumbUrl ?? branding.logo.url} className="h-9 w-11 shrink-0 object-contain brightness-0 invert" alt="" /> : <img src="/favicon.svg" className="size-9 shrink-0" alt="" />}
        {!collapsed && (
          <div className="min-w-0">
            <div className="truncate font-display text-[15px] font-semibold text-white">{branding?.name ?? me?.tenant.name ?? 'Stable'}</div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-sidebar-muted">Admin</div>
          </div>
        )}
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4 [scrollbar-color:hsl(var(--sidebar-active))_transparent]">
        {nav.map((section) => (
          <div key={section.label}>
            {!collapsed && <div className="mb-1.5 px-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-sidebar-muted">{t(section.label)}</div>}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <SideLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
        {isPlatformAdmin && (
          <div>
            {!collapsed && <div className="mb-1.5 px-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-sidebar-muted">{t('Platform')}</div>}
            {PLATFORM_NAV.map((item) => (
              <SideLink key={item.to} item={item} collapsed={collapsed} />
            ))}
          </div>
        )}
      </nav>
      {onToggle && (
        <button onClick={onToggle} className="flex h-11 items-center gap-2 border-t border-white/5 px-5 text-[12px] text-sidebar-muted hover:text-white">
          <ChevronsLeft className={cn('size-4 transition-transform', collapsed && 'rotate-180', 'rtl:rotate-180', collapsed && 'rtl:rotate-0')} />
          {!collapsed && 'Collapse'}
        </button>
      )}
    </>
  );
}

function SideLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { t } = useI18n();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/' || item.to === '/platform'}
      title={collapsed ? t(item.label) : undefined}
      className={({ isActive }) =>
        cn(
          'group flex h-9 items-center gap-3 rounded-lg px-2.5 text-[13.5px] transition-colors',
          isActive ? 'bg-sidebar-active text-white shadow-[inset_2px_0_0_hsl(var(--primary))] rtl:shadow-[inset_-2px_0_0_hsl(var(--primary))]' : 'text-sidebar-foreground/75 hover:bg-white/5 hover:text-white',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <Icon className="size-[17px] shrink-0" />
      {!collapsed && <span className="truncate">{t(item.label)}</span>}
    </NavLink>
  );
}

function TopBar({ onMenu, onSearch }: { onMenu(): void; onSearch(): void }) {
  const { me, logout, selectTenant } = useAuth();
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur-md sm:px-6 lg:px-8">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} aria-label="Menu">
        <Menu />
      </Button>
      <button onClick={onSearch} className="flex h-9 min-w-0 w-full max-w-md items-center gap-2 rounded-lg border bg-surface px-3 text-[13px] text-muted-foreground shadow-sm transition-colors hover:border-ring/50">
        <Search className="size-4 shrink-0" />
        <span className="flex-1 truncate text-start">
          {t('Search')}…<span className="hidden sm:inline"> people, horses, pages</span>
        </span>
        <span className="hidden sm:inline-flex">
          <Kbd>{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</Kbd>
        </span>
      </button>
      <div className="ms-auto flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={() => setLang(lang === 'en' ? 'ar' : 'en')} aria-label={t('Language')} title={lang === 'en' ? 'العربية' : 'English'}>
          <Languages />
        </Button>
        <Button variant="ghost" size="icon" onClick={toggle} aria-label={t('Theme')}>
          {theme === 'dark' ? <Sun /> : <Moon />}
        </Button>
        <DropdownMenu>
          <DropdownTrigger asChild>
            <button className="ms-1 flex items-center gap-2 rounded-lg p-1 pe-2 hover:bg-muted">
              <Avatar size={30} first={me?.person?.firstName ?? me?.user.email ?? me?.user.phone} last={me?.person?.lastName} />
              <span className="hidden text-start leading-tight sm:block">
                <span className="block text-[13px] font-medium">{me ? (me.person ? `${me.person.firstName} ${me.person.lastName}` : me.user.email ?? 'Platform admin') : '…'}</span>
                <span className="block text-[11px] capitalize text-muted-foreground">{me?.roles.filter((r) => !['rider', 'guardian', 'horse_owner'].includes(r)).join(', ').replace(/_/g, ' ') || (me?.user.isPlatformAdmin ? 'platform admin' : '')}</span>
              </span>
              <ChevronsUpDown className="size-3.5 text-muted-foreground" />
            </button>
          </DropdownTrigger>
          <DropdownContent>
            <DropdownLabel>{me?.tenant.name}</DropdownLabel>
            <DropdownItem onSelect={() => { selectTenant(null); navigate('/'); }}>
              <ChevronsUpDown /> {t('Switch stable')}
            </DropdownItem>
            <DropdownItem onSelect={() => navigate('/explorer')}>
              <TerminalSquare /> API explorer
            </DropdownItem>
            {me?.user.isPlatformAdmin && (
              <DropdownItem onSelect={() => navigate('/platform')}>
                <Shield /> Platform console
              </DropdownItem>
            )}
            <DropdownSeparator />
            <DropdownItem destructive onSelect={() => void logout()}>
              <LogOut /> {t('Sign out')}
            </DropdownItem>
          </DropdownContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const nav = useVisibleNav();
  const [q, setQ] = useState('');
  const people = useQuery({ queryKey: ['palette-people', q], queryFn: () => loadEntityList(ENTITIES.person, q), enabled: open && q.length >= 2 });
  const horses = useQuery({ queryKey: ['palette-horses', q], queryFn: () => loadEntityList(ENTITIES.horse, q), enabled: open && q.length >= 2 });
  const go = (to: string) => {
    onOpenChange(false);
    setQ('');
    navigate(to);
  };
  const Item = ({ value, onSelect, children }: { value: string; onSelect(): void; children: ReactNode }) => (
    <Command.Item value={value} onSelect={onSelect} className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] data-[selected=true]:bg-muted [&_svg]:size-4 [&_svg]:text-muted-foreground">
      {children}
    </Command.Item>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('Command palette')} size="md">
      <Command className="-mx-2 -my-2" loop>
        <div className="mb-2 flex items-center gap-2 rounded-lg border px-3">
          <Search className="size-4 text-muted-foreground" />
          <Command.Input autoFocus value={q} onValueChange={setQ} placeholder="Jump to a page, person or horse…" className="h-11 flex-1 bg-transparent outline-none" />
        </div>
        <Command.List className="max-h-[52vh] overflow-y-auto">
          <Command.Empty className="py-8 text-center text-sm text-muted-foreground">No results</Command.Empty>
          {(people.data ?? []).length > 0 && (
            <Command.Group heading="People" className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground [&_[cmdk-group-items]]:mt-1 [&_[cmdk-group-items]]:normal-case [&_[cmdk-group-items]]:tracking-normal">
              {people.data!.map((p) => (
                <Item key={String(p.id)} value={`person ${p.firstName} ${p.lastName} ${p.phone ?? ''}`} onSelect={() => go(`/people/${p.id}`)}>
                  <Avatar size={22} first={String(p.firstName)} last={String(p.lastName)} />
                  <span className="font-medium text-foreground">{`${p.firstName} ${p.lastName}`}</span>
                  <span className="text-xs text-muted-foreground">{String(p.phone ?? '')}</span>
                </Item>
              ))}
            </Command.Group>
          )}
          {(horses.data ?? []).length > 0 && (
            <Command.Group heading="Horses" className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground [&_[cmdk-group-items]]:mt-1 [&_[cmdk-group-items]]:normal-case [&_[cmdk-group-items]]:tracking-normal">
              {horses.data!.map((h) => (
                <Item key={String(h.id)} value={`horse ${h.name}`} onSelect={() => go(`/horses/${h.id}`)}>
                  <span className="font-medium text-foreground">{String(h.name)}</span>
                  <span className="text-xs text-muted-foreground">{String(h.breed ?? '')}</span>
                </Item>
              ))}
            </Command.Group>
          )}
          {nav.map((s) => (
            <Command.Group key={s.label} heading={t(s.label)} className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground [&_[cmdk-group-items]]:mt-1 [&_[cmdk-group-items]]:normal-case [&_[cmdk-group-items]]:tracking-normal">
              {s.items.map((i) => (
                <Item key={i.to} value={`${t(i.label)} ${i.label}`} onSelect={() => go(i.to)}>
                  <i.icon />
                  <span className="text-foreground">{t(i.label)}</span>
                </Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command>
    </Dialog>
  );
}
